import type { Kontakt, Kontaktbereich, KontaktEingabe } from '@nac2s/shared'
import { KONTAKTFELDNAMEN, kontaktfelder } from '@nac2s/shared'
import { type FormEvent, useId, useState } from 'react'
import { Dialogfenster } from '@/teile/Dialogfenster'
import { Knopf } from '@/teile/Knopf'
import { Fehler } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import { kontaktAendern, kontaktAnlegen } from '../api/kontakte.js'

/**
 * Einen Kontakt anlegen oder ändern (ADR-0016, Punkt 1).
 *
 * Die Felder stehen in `kontaktfelder` — dieselbe Liste, die der CSV-Import
 * als Ziele anbietet. Zwei Listen wären zwei Wahrheiten darüber, was ein
 * Kontakt hat.
 */

const MEHRZEILIG: ReadonlySet<string> = new Set(['bemerkung'])

function leer(): Record<string, string> {
  return Object.fromEntries(kontaktfelder.map((f) => [f, '']))
}

function ausKontakt(k: Kontakt): Record<string, string> {
  return Object.fromEntries(kontaktfelder.map((f) => [f, k[f] ?? '']))
}

export function KontaktFormular({
  bereich,
  kontakt,
  onFertig,
  onAbbruch,
}: {
  bereich: Kontaktbereich
  /** Gesetzt: ändern. Fehlt: anlegen. */
  kontakt?: Kontakt
  onFertig: () => void
  onAbbruch: () => void
}) {
  const [werte, setWerte] = useState<Record<string, string>>(() =>
    kontakt ? ausKontakt(kontakt) : leer(),
  )
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const formularId = useId()
  const vollstaendig = Boolean(
    werte.firma?.trim() || werte.nachname?.trim() || werte.vorname?.trim(),
  )

  async function absenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    if (!vollstaendig || laeuft) return
    setLaeuft(true)
    setMeldung(null)
    const eingabe = Object.fromEntries(
      kontaktfelder.map((f) => [f, werte[f]?.trim() ?? '']),
    ) as unknown as KontaktEingabe
    try {
      if (kontakt) await kontaktAendern(bereich, kontakt.id, eingabe)
      else await kontaktAnlegen(bereich, eingabe)
      onFertig()
    } catch (fehler) {
      setMeldung(
        fehler instanceof ApiError && fehler.status === 403
          ? 'Zentrale Kontakte dürfen Sie nicht pflegen.'
          : 'Nicht gespeichert.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Dialogfenster titel={kontakt ? 'Kontakt ändern' : 'Kontakt anlegen'} onSchliessen={onAbbruch}>
      <form onSubmit={absenden} className="flex flex-col gap-4 pb-6">
        <div className="grid gap-3 sm:grid-cols-2">
          {kontaktfelder.map((feld) => (
            <div
              key={feld}
              className={`flex flex-col gap-1.5 ${MEHRZEILIG.has(feld) ? 'sm:col-span-2' : ''}`}
            >
              <label
                htmlFor={`${formularId}-${feld}`}
                className="text-[11.5px] font-medium text-quaternary"
              >
                {KONTAKTFELDNAMEN[feld]}
              </label>
              {MEHRZEILIG.has(feld) ? (
                <textarea
                  id={`${formularId}-${feld}`}
                  rows={3}
                  value={werte[feld] ?? ''}
                  onChange={(e) => setWerte((v) => ({ ...v, [feld]: e.target.value }))}
                  className="rounded-[10px] border border-secondary bg-primary px-3 py-2 text-sm text-primary outline-none focus:border-rand-aktiv"
                />
              ) : (
                <input
                  id={`${formularId}-${feld}`}
                  type={feld === 'email' ? 'email' : 'text'}
                  value={werte[feld] ?? ''}
                  onChange={(e) => setWerte((v) => ({ ...v, [feld]: e.target.value }))}
                  className="rounded-[10px] border border-secondary bg-primary px-3 py-2 text-sm text-primary outline-none focus:border-rand-aktiv"
                />
              )}
            </div>
          ))}
        </div>

        {meldung ? <Fehler>{meldung}</Fehler> : null}

        <div className="flex items-center justify-end gap-2.5 border-t border-secondary pt-4">
          <Knopf onClick={onAbbruch} disabled={laeuft}>
            Abbrechen
          </Knopf>
          <Knopf type="submit" stark disabled={!vollstaendig || laeuft}>
            {laeuft ? 'Wird gespeichert …' : 'Speichern'}
          </Knopf>
        </div>
      </form>
    </Dialogfenster>
  )
}
